import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicFormPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadForm();
  }, [slug]);

  const loadForm = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/form/${slug}`);
      setForm(response.data.form);
      setFields(response.data.fields || []);
      
      // Initialize form data
      const initialData = {};
      (response.data.fields || []).forEach(field => {
        if (field.field_type === 'checkbox') {
          initialData[field.field_key] = [];
        } else {
          initialData[field.field_key] = '';
        }
      });
      setFormData(initialData);
    } catch (err) {
      setError('Form not found or unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
  };

  const handleCheckboxChange = (fieldKey, optionValue, checked) => {
    setFormData(prev => {
      const current = prev[fieldKey] || [];
      if (checked) {
        return { ...prev, [fieldKey]: [...current, optionValue] };
      } else {
        return { ...prev, [fieldKey]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/public/forms/${slug}/submit`, {
        data: formData,
        honeypot: '', // Empty honeypot
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getLabel = (field) => {
    if (language === 'es' && field.label_es) {
      return field.label_es;
    }
    return field.label;
  };

  const getPlaceholder = (field) => {
    if (language === 'es' && field.placeholder_es) {
      return field.placeholder_es;
    }
    return field.placeholder || '';
  };

  const getOptionLabel = (option) => {
    if (language === 'es' && option.label_es) {
      return option.label_es;
    }
    return option.label;
  };

  const renderField = (field) => {
    const label = getLabel(field);
    const placeholder = getPlaceholder(field);

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
              value={formData[field.field_key] || ''}
              onChange={(e) => handleChange(field.field_key, e.target.value)}
              placeholder={placeholder}
              required={field.required}
              className="border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={formData[field.field_key] || ''}
              onChange={(e) => handleChange(field.field_key, e.target.value)}
              placeholder={placeholder}
              required={field.required}
              rows={4}
              className="border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
            />
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={formData[field.field_key] || ''}
              onValueChange={(val) => handleChange(field.field_key, val)}
              required={field.required}
            >
              <SelectTrigger className="border-[#E8E4DC]">
                <SelectValue placeholder={placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options_json || []).map((option, idx) => (
                  <SelectItem key={idx} value={option.value}>
                    {getOptionLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {(field.options_json || []).map((option, idx) => (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={field.field_key}
                    value={option.value}
                    checked={formData[field.field_key] === option.value}
                    onChange={(e) => handleChange(field.field_key, e.target.value)}
                    required={field.required}
                    className="w-4 h-4 text-[#1F2A44] border-[#E8E4DC] focus:ring-[#C2A46D]"
                  />
                  <span className="text-[#555555]">{getOptionLabel(option)}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className="space-y-2">
              {(field.options_json || []).map((option, idx) => (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={(formData[field.field_key] || []).includes(option.value)}
                    onChange={(e) => handleCheckboxChange(field.field_key, option.value, e.target.checked)}
                    className="w-4 h-4 text-[#1F2A44] border-[#E8E4DC] focus:ring-[#C2A46D] rounded"
                  />
                  <span className="text-[#555555]">{getOptionLabel(option)}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'consent':
        return (
          <div key={field.id} className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData[field.field_key] === 'yes'}
                onChange={(e) => handleChange(field.field_key, e.target.checked ? 'yes' : '')}
                required={field.required}
                className="w-4 h-4 mt-1 text-[#1F2A44] border-[#E8E4DC] focus:ring-[#C2A46D] rounded"
              />
              <span className="text-[#555555] text-sm">
                {label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-[#222222]">
              {label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="date"
              value={formData[field.field_key] || ''}
              onChange={(e) => handleChange(field.field_key, e.target.value)}
              required={field.required}
              className="border-[#E8E4DC] focus:border-[#C2A46D] focus:ring-[#C2A46D]"
            />
          </div>
        );

      case 'hidden':
        return (
          <input
            key={field.id}
            type="hidden"
            name={field.field_key}
            value={formData[field.field_key] || field.placeholder || ''}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F2A44]" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-[#222222] mb-2">Form Not Found</h1>
          <p className="text-[#555555]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const successMessage = language === 'es' 
      ? (form.settings_json?.success_message_es || '¡Gracias por su envío!')
      : (form.settings_json?.success_message || 'Thank you for your submission!');
    
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-serif text-[#222222] mb-4">{successMessage}</h1>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({});
              fields.forEach(field => {
                if (field.field_type === 'checkbox') {
                  formData[field.field_key] = [];
                } else {
                  formData[field.field_key] = '';
                }
              });
            }}
            variant="outline"
            className="border-[#1F2A44] text-[#1F2A44]"
          >
            {language === 'es' ? 'Enviar otra respuesta' : 'Submit another response'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFA] py-12 px-4" data-testid="public-form-page">
      <div className="max-w-xl mx-auto">
        {/* Form Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-[#222222] mb-2">{form.name}</h1>
          {form.description && (
            <p className="text-[#555555]">{form.description}</p>
          )}
          <div className="w-16 h-1 bg-[#C2A46D] mx-auto mt-4"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-[#E8E4DC] rounded-lg p-6 md:p-8 shadow-sm">
          <div className="space-y-6">
            {fields
              .filter(f => f.is_enabled)
              .sort((a, b) => a.order_index - b.order_index)
              .map(field => renderField(field))}
          </div>

          {/* Honeypot field (hidden) */}
          <input
            type="text"
            name="website"
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mt-8">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1F2A44] hover:bg-[#151D30] text-white py-3"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'es' ? 'Enviando...' : 'Submitting...'}
                </>
              ) : (
                language === 'es' ? 'Enviar' : 'Submit'
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-[#777777]">
            {language === 'es' 
              ? 'Su información está segura y protegida.'
              : 'Your information is safe and secure.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicFormPage;
